
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAgentConfig } from '../contexts/AgentConfigContext';
import { useAuth } from '../contexts/AuthContext';
import ServerSidePagination from '../common_jsx/ServerSidePagination';
import { parseMarkdownTable, parseHTMLTable } from '../utils/tableParser';
import { sanitizeHTML } from '../utils/sanitize';



/**
 * ServerSideDataTable Component
 * 
 * Table component with server-side pagination for large datasets (total_count > 30).
 * Uses the /agents/<agent_id>/pagination endpoint for fetching data.
 */
const ServerSideDataTable = ({
  initialData,
  totalCount,
  messageId,
  sessionId,
  sourceKey,
  onPaginationStateChange,
}) => {
  const { isDarkMode } = useDarkMode();
  const { config } = useAgentConfig();
  const { getUserIdDiscover, user } = useAuth();
  
  const [displayData, setDisplayData] = useState(initialData || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localTotalCount, setLocalTotalCount] = useState(totalCount);
  // Client-side cache for visited pages: { [pageNum-rowsPerPage]: data[] }
  const pageCache = React.useRef({});
  // Store initialData in ref so we can use it for re-caching when rowsPerPage changes
  const initialDataRef = React.useRef(initialData || []);
  
  // Update initialDataRef when initialData changes
  React.useEffect(() => {
    if (initialData && initialData.length > 0) {
      initialDataRef.current = initialData;
    }
  }, [initialData]);

  // Filter out excluded fields
  const excludedFields = ["row_num"];
  const headers = displayData && displayData.length > 0 
    ? Object.keys(displayData[0]).filter(header => !excludedFields.includes(header))
    : [];

  // Helper function to get data from initialData cache if available
  // Returns data only if ALL requested rows are available in initialData
  const getDataFromInitialCache = useCallback((page, limit) => {
    const startIdx = (page - 1) * limit;
    const endIdx = startIdx + limit;
    const cachedData = initialDataRef.current;
    
    // Check if all requested rows are within the initialData
    if (cachedData && cachedData.length > 0 && startIdx < cachedData.length) {
      const pageData = cachedData.slice(startIdx, Math.min(endIdx, cachedData.length));
      
      // Only use cache if:
      // 1. We have a full page of data, OR
      // 2. This is genuinely the last page of ALL data (endIdx >= localTotalCount)
      const hasFullPage = pageData.length === limit;
      const isLastPageOfAllData = endIdx >= localTotalCount;
      
      if (pageData.length > 0 && (hasFullPage || isLastPageOfAllData)) {
        // But if limit > cachedData.length and this is page 1, we need API call
        if (page === 1 && limit > cachedData.length) {
          return null; // Need to fetch more data from API
        }
        return pageData;
      }
    }
    return null;
  }, [localTotalCount]);

  // Fetch data from server
  const fetchPageData = useCallback(async (page, limit) => {
    if (!messageId || !sessionId) {
      console.error('Missing messageId or sessionId for pagination');
      return;
    }

    const cacheKey = `${page}-${limit}`;
    // If page is cached, use it instantly
    // But verify cached data is a full page OR it's genuinely the last page of all data
    if (pageCache.current[cacheKey]) {
      const cachedPageData = pageCache.current[cacheKey];
      const endIdx = page * limit;
      const isLastPageOfAllData = endIdx >= localTotalCount;
      const hasFullPage = cachedPageData.length === limit;
      
      if (hasFullPage || isLastPageOfAllData) {
        setDisplayData(cachedPageData);
        setCurrentPage(page);
        setIsLoading(false);
        setError(null);
        return;
      }
      // Cache has partial data but more exists on server - delete stale cache entry
      delete pageCache.current[cacheKey];
    }
    
    // Check if data is available from initialData (session API data)
    // This avoids API calls for first 30 rows which are already loaded
    const initialCacheData = getDataFromInitialCache(page, limit);
    if (initialCacheData && initialCacheData.length > 0) {
      // Cache it and use it
      pageCache.current[cacheKey] = initialCacheData;
      setDisplayData(initialCacheData);
      setCurrentPage(page);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userId = getUserIdDiscover();
      const userEmail = user?.email;

      const requestBody = {
        message_id: messageId,
        user_id: userId,
        session_id: sessionId,
        user_email: userEmail,
        page: page,
        page_size: limit,
        ...(sourceKey && { source_key: sourceKey }),
      };

      const response = await axios.post(
        `${config.serviceUrl}/agents/${config.agentId}/pagination`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.apiKey}`,
          },
        }
      );

      // Parse response — new format: { answer: { data_sources: { SOURCE: html/markdown } }, page: N }
      // Legacy format: { code: 0, data: { results: [...], total_count: N } }
      let parsedResults = [];
      let total_count;

      // Helper: parse a content string as HTML table or markdown table
      const parseContentString = (content) => {
        if (typeof content !== 'string') return [];
        // Use includes('<table') so HTML is detected even with leading headings/text
        if (content.includes('<table')) return parseHTMLTable(content);
        if (content.includes('|')) return parseMarkdownTable(content);
        return [];
      };

      // Actual response format: { code: 0, data: { answer: { data_sources: {...} }, total_count: N } }
      if (response.data?.code === 0) {
        const responseData = response.data.data;
        total_count = responseData?.total_count;
        // Handle dict total_count: extract scalar for this source key
        if (total_count && typeof total_count === 'object' && sourceKey) {
          total_count = total_count[sourceKey];
        }

        if (responseData?.answer?.data_sources) {
          if (sourceKey && responseData.answer.data_sources[sourceKey]) {
            // Extract only this source's content
            parsedResults = parseContentString(responseData.answer.data_sources[sourceKey]);
          } else {
            // Fallback: concatenate all sources
            for (const content of Object.values(responseData.answer.data_sources)) {
              parsedResults = parsedResults.concat(parseContentString(content));
            }
          }
        } else if (typeof responseData?.answer === 'string') {
          parsedResults = parseContentString(responseData.answer);
        } else if (responseData?.results !== undefined) {
          // Legacy results format
          const results = responseData.results;
          if (Array.isArray(results)) {
            parsedResults = results;
          } else if (typeof results === 'string') {
            try {
              const parsed = JSON.parse(results);
              parsedResults = Array.isArray(parsed) ? parsed : (typeof parsed === 'object' && parsed !== null ? [parsed] : []);
            } catch (e) {
              parsedResults = parseContentString(results);
            }
          } else if (typeof results === 'object' && results !== null) {
            parsedResults = [results];
          }
        }
      } else if (response.data?.answer?.data_sources) {
        // Root-level answer.data_sources (alternative format without code wrapper)
        for (const content of Object.values(response.data.answer.data_sources)) {
          parsedResults = parsedResults.concat(parseContentString(content));
        }
      } else if (typeof response.data?.answer === 'string') {
        parsedResults = parseContentString(response.data.answer);
      } else {
        console.warn('Pagination API returned unrecognized format:', response.data);
      }

      // Cache the result for this page
      pageCache.current[cacheKey] = parsedResults;
      setDisplayData(parsedResults);
      setCurrentPage(page);

      // Update total count if returned from server (legacy format)
      if (total_count !== undefined && total_count !== null) {
        setLocalTotalCount(total_count);
      }
    } catch (err) {
      console.error('Error fetching pagination data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [messageId, sessionId, sourceKey, config, getUserIdDiscover, user, getDataFromInitialCache, localTotalCount]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    // Prevent concurrent fetches or redundant calls
    if (newPage !== currentPage && !isLoading) {
      fetchPageData(newPage, rowsPerPage);
    }
  }, [currentPage, rowsPerPage, isLoading, fetchPageData]);

  // Handle rows per page change
  // User stays on the same page number, only the data range changes
  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    // Prevent concurrent fetches or redundant calls
    if (newRowsPerPage !== rowsPerPage && !isLoading) {
      // Keep the same page number, just change the data displayed
      const targetPage = currentPage;
      
      // Calculate the new total pages with new rows per page
      const newTotalPages = Math.ceil(localTotalCount / newRowsPerPage);
      // If current page exceeds new total pages, go to the last page
      const newPage = Math.min(targetPage, newTotalPages);
      
      // Update rowsPerPage state
      setRowsPerPage(newRowsPerPage);
      // Update currentPage if needed
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
      
      // Fetch data for the page with new rows per page
      const cacheKey = `${newPage}-${newRowsPerPage}`;
      // Check cache first
      if (pageCache.current[cacheKey]) {
        setDisplayData(pageCache.current[cacheKey]);
        return;
      }
      // Check initialData cache
      const startIdx = (newPage - 1) * newRowsPerPage;
      const endIdx = startIdx + newRowsPerPage;
      const cachedData = initialDataRef.current;
      if (cachedData && cachedData.length > 0 && startIdx < cachedData.length) {
        const pageData = cachedData.slice(startIdx, Math.min(endIdx, cachedData.length));
        // Only use cache if:
        // 1. We have a full page of data, OR
        // 2. This is genuinely the last page (endIdx >= totalCount means no more data on server)
        const isLastPageOfAllData = endIdx >= localTotalCount;
        const hasFullPage = pageData.length === newRowsPerPage;
        
        if (pageData.length > 0 && (hasFullPage || isLastPageOfAllData)) {
          // Don't use partial cache if more data exists on server
          if (!(newPage === 1 && newRowsPerPage > cachedData.length)) {
            pageCache.current[cacheKey] = pageData;
            setDisplayData(pageData);
            return;
          }
        }
      }
      // Fetch from API if not in cache or cache is incomplete
      fetchPageData(newPage, newRowsPerPage);
    }
  }, [rowsPerPage, currentPage, localTotalCount, isLoading, fetchPageData]);

  // Update pagination state for parent component
  useEffect(() => {
    if (onPaginationStateChange) {
      const totalPages = Math.ceil(localTotalCount / rowsPerPage);
      const startRecord = (currentPage - 1) * rowsPerPage + 1;
      const endRecord = Math.min(currentPage * rowsPerPage, localTotalCount);

      onPaginationStateChange({
        isLoading,
        pageNum: currentPage,
        totalPages,
        startRecord,
        endRecord,
        totalRows: localTotalCount,
        showPagination: true,
        isServerSide: true,
      });
    }
  }, [isLoading, currentPage, rowsPerPage, localTotalCount, onPaginationStateChange]);

  // Initialize with initial data and cache it for page 1
  // This data comes from the session API and represents the first batch of rows (up to 30)
  // We slice it to only show rowsPerPage rows initially
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      // Only display rowsPerPage rows, not all initialData
      const dataToDisplay = initialData.slice(0, rowsPerPage);
      setDisplayData(dataToDisplay);
      // Cache the full initial data - we can use it for pages without API calls
      // BUT only cache FULL pages, not partial ones (unless it's the last page of ALL data)
      for (let page = 1; page <= Math.ceil(initialData.length / rowsPerPage); page++) {
        const startIdx = (page - 1) * rowsPerPage;
        const endIdx = startIdx + rowsPerPage;
        const pageData = initialData.slice(startIdx, Math.min(endIdx, initialData.length));
        
        // Only cache if it's a full page OR this is genuinely the last page of all server data
        const hasFullPage = pageData.length === rowsPerPage;
        const isLastPageOfAllData = endIdx >= localTotalCount;
        
        if (pageData.length > 0 && (hasFullPage || isLastPageOfAllData)) {
          pageCache.current[`${page}-${rowsPerPage}`] = pageData;
        }
      }
    }
  }, [initialData, rowsPerPage, localTotalCount]);


  // Update total count when prop changes
  useEffect(() => {
    if (totalCount !== undefined && totalCount !== null) {
      setLocalTotalCount(totalCount);
    }
  }, [totalCount]);

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${
        isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'
      }`}>
        <p className="text-sm font-medium">Error loading data</p>
        <p className="text-xs mt-1">{error}</p>
        <button
          onClick={() => fetchPageData(currentPage, rowsPerPage)}
          className={`mt-2 px-3 py-1 rounded text-xs font-medium transition-colors ${
            isDarkMode 
              ? 'bg-red-800 hover:bg-red-700 text-white' 
              : 'bg-red-100 hover:bg-red-200 text-red-700'
          }`}
        >
          Retry
        </button>
      </div>
    );
  }

  // Show loading state when fetching initial data
  if ((!displayData || displayData.length === 0) && isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${
        isDarkMode ? 'text-slate-400' : 'text-gray-600'
      }`}>
        <svg className="animate-spin h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24">
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className="text-sm">Loading data...</span>
      </div>
    );
  }

  if (!displayData || displayData.length === 0) {
    return (
      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
        No records to display
      </p>
    );
  }

  return (
    <div className="w-full relative">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none" style={{ minHeight: '400px' }}>
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-12 w-12" fill="none" viewBox="0 0 24 24">
              <circle 
                className="opacity-25" 
                cx="12" 
                cy="12" 
                r="10" 
                stroke="currentColor" 
                strokeWidth="4"
                style={{ stroke: isDarkMode ? '#60a5fa' : '#3b82f6' }}
              />
              <path 
                className="opacity-75" 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                style={{ fill: isDarkMode ? '#60a5fa' : '#3b82f6' }}
              />
            </svg>
          </div>
        </div>
      )}
      
      {/* Table with horizontal scroll */}
      <div className="overflow-x-auto table-scrollbar">
        <table className={`min-w-full divide-y ${
          isDarkMode ? 'divide-slate-700' : 'divide-gray-200'
        }`}>
          <thead className={isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}>
            <tr>
              {headers.map((header, headerIndex) => (
                <th 
                  key={`header-${header}-${headerIndex}`} 
                  className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                    isDarkMode ? 'text-white' : 'text-gray-700'
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y ${
            isDarkMode ? 'divide-slate-700 bg-slate-900' : 'divide-gray-200 bg-white'
          }`}>
            {displayData.map((row, rowIndex) => (
              <tr 
                key={`row-${rowIndex}`} 
                className={`${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}`}
              >
                {headers.map((header, cellIndex) => {
                  const cellValue = row[header];
                  const raw = cellValue === null || cellValue === undefined
                    ? ''
                    : typeof cellValue === 'object'
                      ? JSON.stringify(cellValue)
                      : String(cellValue);
                  // Convert markdown links [text](url) to HTML anchors
                  const withLinks = raw.replace(
                    /\[([^\]]+)\]\(([^)]+)\)/g,
                    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#3b82f6;text-decoration:underline">$1</a>'
                  );
                  const isHTML = withLinks !== raw || (typeof cellValue === 'string' && /<[a-z][\s\S]*>/i.test(cellValue));
                  return (
                    <td
                      key={`cell-${rowIndex}-${cellIndex}`}
                      className={`px-4 py-3 text-sm ${isHTML ? '' : 'whitespace-nowrap'} ${
                        isDarkMode ? 'text-slate-300' : 'text-gray-900'
                      }`}
                    >
                      {isHTML ? (
                        <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(withLinks) }} />
                      ) : (
                        raw
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Server-side pagination controls — hidden when all data fits on one page */}
      {Math.ceil(localTotalCount / rowsPerPage) > 1 && (
        <ServerSidePagination
          totalCount={localTotalCount}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          isLoading={isLoading}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </div>
  );
};

ServerSideDataTable.propTypes = {
  initialData: PropTypes.array,
  totalCount: PropTypes.number.isRequired,
  messageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sourceKey: PropTypes.string,
  onPaginationStateChange: PropTypes.func,
};

ServerSideDataTable.defaultProps = {
  initialData: [],
  sourceKey: null,
  onPaginationStateChange: null,
};

export default ServerSideDataTable;
